import { Tab, Tabs, alpha, Stack, Divider, Typography } from '@mui/material';
import BackButton from 'src/components/ui-kit/back-button';
import { useState, useEffect, useCallback } from 'react';
import LoadingContainer from 'src/components/ui-kit/loading-container';
import { useBoolean } from 'src/hooks/use-boolean';
import { UserType, UserVerificationStatus } from 'src/type/user';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import NotFoundContainer from 'src/components/ui-kit/not-found-container';
import { isEmpty } from 'lodash';
import Label from 'src/components/label';
import InfoGrid from 'src/components/ui-kit/info-grid';

import { useSearchParams } from 'react-router-dom';
import UserEditVerification from '../user-edit-verification';
import UserEditForm from '../user-edit-form';
import { verificationStatusLabel } from '../utils';
import PositionTable from '../tabs/position/position-table';
import RedeemTableView from '../tabs/redeem/redeem-view';
import DepositTableView from '../tabs/deposit/deposit-view';
import TopupTableView from '../tabs/topup-deposit/topup-view';
import Portfolio from '../tabs/portfolio/portfolio';
import Verification from '../tabs/verification/verification';
import TopupWithdrawView from '../tabs/topup-withdraw/topup-withdraw-view';

type Props = {
  id: string;
};

const tabs = [
  {
    value: 'portfolio',
    label: 'Portfolio',
  },
  {
    value: 'position',
    label: 'ตัดซื้อ/ขาย',
  },
  {
    value: 'redeem',
    label: 'ถอนสินค้า',
  },
  {
    value: 'deposit',
    label: 'ฝากสินค้า',
  },
  {
    value: 'topup-deposit',
    label: 'วงเงินหลักประกัน',
  },
  {
    value: 'topup-withdraw',
    label: 'เติมเงิน/ถอนเงิน',
  },
  {
    value: 'update-info',
    label: 'อัพเดทข้อมูล',
  },
  {
    value: 'verify',
    label: 'การยืนยันตัวตน',
  },
];

export default function UserEditView({ id }: Props) {
  const initialize = useBoolean(true);
  const [user, setUser] = useState<UserType | null>(null);
  const reload = useBoolean();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(() => {
    const paramTab = searchParams.get('tab');
    if (!paramTab) {
      return tabs[0].value;
    }
    const tab = tabs.find((t) => t.value === paramTab);
    return tab ? paramTab : tabs[0].value;
  });

  useEffect(() => {
    load();
  }, [reload.value]);

  const load = async () => {
    try {
      // initialize.onTrue();
      const { data } = await axios.get(endpoints.users.getById(id));
      setUser(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  };

  const handleChangeTab = useCallback((tab: string) => {
    setSearchParams({ tab }, { replace: true });
    setActiveTab(tab);
  }, []);

  const handleReload = () => {
    reload.onToggle();
  };

  const renderSection = () => {
    if (!user) return null;
    if (user.verifyStatus === UserVerificationStatus.REJECTED) {
      return null;
    }

    if (user.verifyStatus === UserVerificationStatus.VERIFIED) {
      return (
        <Stack spacing={2} sx={{ minHeight: '80vh' }}>
          <Tabs
            value={activeTab}
            onChange={(_e, newValue) => handleChangeTab(newValue)}
            sx={{
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          {activeTab === 'portfolio' && <Portfolio user={user} rootReload={handleReload} />}
          {activeTab === 'position' && <PositionTable user={user} rootReload={handleReload} />}
          {activeTab === 'redeem' && (
            <RedeemTableView
              user={user}
              rootReload={handleReload}
              enableCreate
              isCreateAndApprove
            />
          )}
          {activeTab === 'deposit' && (
            <DepositTableView
              user={user}
              rootReload={handleReload}
              enableCreate
              isCreateAndApprove
            />
          )}
          {activeTab === 'topup-deposit' && (
            <TopupTableView user={user} rootReload={handleReload} enableCreate isCreateAndApprove />
          )}
          {activeTab === 'topup-withdraw' && (
            <TopupWithdrawView user={user} rootReload={handleReload} />
          )}

          {activeTab === 'update-info' && (
            <Stack sx={{ mt: 3 }}>
              <UserEditForm
                user={user}
                handleReload={handleReload}
                onUserUpdate={(updatedUser) => setUser(updatedUser)}
              />
            </Stack>
          )}
          {activeTab === 'verify' && <Verification user={user} />}
        </Stack>
      );
    }

    return (
      <Stack>
        <Divider sx={{ my: 2 }} />
        <UserEditForm
          user={user}
          handleReload={handleReload}
          onUserUpdate={(updatedUser) => setUser(updatedUser)}
        />
        <Divider sx={{ my: 3 }} />
        <UserEditVerification user={user} handleReload={handleReload} />
      </Stack>
    );
  };

  const userInfos = () => {
    if (!user) return [];

    return [
      { label: 'ลูกค้า', value: `${user?.firstName} ${user?.lastName}` },
      {
        label: 'สถานะยืนยันตัวตน',
        value: (
          <Label
            variant="soft"
            color={
              (user.verifyStatus === UserVerificationStatus.VERIFIED && 'success') ||
              (user.verifyStatus === UserVerificationStatus.PENDING && 'info') ||
              (user.verifyStatus === UserVerificationStatus.PROCESSING && 'secondary') ||
              (user.verifyStatus === UserVerificationStatus.REJECTED && 'error') ||
              'default'
            }
          >
            {verificationStatusLabel(user.verifyStatus)}
          </Label>
        ),
      },
      {
        label: 'สถานะบัญชี',
        value: (
          <Label variant="soft" color={(user.isEnable === true && 'success') || 'error'}>
            {user.isEnable === true ? 'ปกติ' : 'โดนแบน'}
          </Label>
        ),
      },
    ];
  };

  return (
    <LoadingContainer loading={initialize.value}>
      <NotFoundContainer notFound={!user}>
        {!isEmpty(user) && (
          <>
            <BackButton />
            <Typography variant="h4">ข้อมูลผู้ใช้งาน</Typography>

            <Stack sx={{ width: { xs: '100%', sm: '50%' } }}>
              <InfoGrid infos={userInfos()} />
            </Stack>
            {renderSection()}
          </>
        )}
      </NotFoundContainer>
    </LoadingContainer>
  );
}
